import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["onClick"];
import React, { useCallback, useContext } from 'react';
import { withErrorBoundary as withReactErrorBoundary } from 'react-error-boundary';
import { injectIntl } from 'react-intl-next';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { extractSmartLinkProvider } from '@atlaskit/link-extractors';
import { fg } from '@atlaskit/platform-feature-flags';
import { SmartCardContext } from '../../../state';
import { getFirstPartyIdentifier, getServices, getThirdPartyARI } from '../../../state/helpers';
import useResolveHyperlink from '../../../state/hooks/use-resolve-hyperlink';
import useResolveHyperlinkValidator, { isGoogleDomain, isSharePointDomain } from '../../../state/hooks/use-resolve-hyperlink/useResolveHyperlinkValidator';
import withIntlProvider from '../../common/intl-provider';
import { useFire3PWorkflowsClickEvent } from '../../SmartLinkEvents/useSmartLinkEvents';
import Hyperlink from '../Hyperlink';
import withErrorBoundary from './error-boundary';
import { ResolveHyperlink } from './resolve-hyperlink';
import HyperlinkUnauthorizedView from './unauthorize-view';
var HyperlinkFallbackComponent = function HyperlinkFallbackComponent() {
  return null;
};
var withValidator = function withValidator(Component, DefaultComponent) {
  return function (props) {
    var shouldResolveHyperlink = useResolveHyperlinkValidator(props === null || props === void 0 ? void 0 : props.href);
    return shouldResolveHyperlink ? /*#__PURE__*/React.createElement(Component, props) : /*#__PURE__*/React.createElement(DefaultComponent, props);
  };
};
var HyperlinkWithSmartLinkResolverInner = function HyperlinkWithSmartLinkResolverInner(_ref) {
  var onClickCallback = _ref.onClick,
    props = _objectWithoutProperties(_ref, _excluded);
  var _useResolveHyperlink = useResolveHyperlink({
      href: props.href || ''
    }),
    actions = _useResolveHyperlink.actions,
    state = _useResolveHyperlink.state;
  var services = getServices(state === null || state === void 0 ? void 0 : state.details);
  var thirdPartyARI = getThirdPartyARI(state === null || state === void 0 ? void 0 : state.details);
  var firstPartyIdentifier = getFirstPartyIdentifier();
  var fire3PClickEvent = fg('platform_smartlink_3pclick_analytics') ?
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useFire3PWorkflowsClickEvent(firstPartyIdentifier, thirdPartyARI) : undefined;
  var onClick = useCallback(function (e) {
    // Only fire the event if the feature flag is on and other conditions are met
    if ((state === null || state === void 0 ? void 0 : state.status) === 'resolved' && (e === null || e === void 0 ? void 0 : e.button) === 0 && fire3PClickEvent && fg('platform_smartlink_3pclick_analytics')) {
      // 0 taken from button state representation -
      // https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/button
      fire3PClickEvent();
    }
    onClickCallback === null || onClickCallback === void 0 || onClickCallback(e);
  }, [onClickCallback, fire3PClickEvent, state === null || state === void 0 ? void 0 : state.status]);

  // TODO: AI3W-1113: Show auth button
  var onAuthorize = fg('platform_linking_plain_hyperlink_connect_button') ?
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useCallback(function () {
    return (
      // TODO: Need to add hyperlink type
      actions.authorize('inline')
    );
  }, [actions]) : undefined;
  if (fg('platform_linking_plain_hyperlink_connect_button')) {
    switch (state === null || state === void 0 ? void 0 : state.status) {
      case 'unauthorized':
        var provider = extractSmartLinkProvider(state === null || state === void 0 ? void 0 : state.details);
        return /*#__PURE__*/React.createElement(HyperlinkUnauthorizedView, _extends({}, props, {
          onAuthorize: services !== null && services !== void 0 && services.length ? onAuthorize : undefined,
          onClick: onClick,
          provider: provider
        }));
      default:
        return /*#__PURE__*/React.createElement(Hyperlink, _extends({}, props, {
          onClick: onClick
        }));
    }
  }
  return /*#__PURE__*/React.createElement(Hyperlink, _extends({}, props, {
    onClick: onClick
  }));
};
export var HyperlinkWithSmartLinkResolver = withReactErrorBoundary(withValidator(injectIntl(withIntlProvider(HyperlinkWithSmartLinkResolverInner), {
  enforceContext: false
}), Hyperlink), {
  FallbackComponent: HyperlinkFallbackComponent
});

// Remove on navx-1834-refactor-resolved-hyperlink cleanup

var HyperlinkResolver = function HyperlinkResolver(_ref2) {
  var href = _ref2.href;
  var hasSmartCardProvider = !!useContext(SmartCardContext);
  var isSharePointResolveEnabled = FeatureGates.getExperimentValue('platform_editor_resolve_hyperlinks_confluence', 'isEnabled', false) || FeatureGates.getExperimentValue('platform_editor_resolve_hyperlinks_jira', 'isEnabled', false);
  var isGoogleResolveEnabled = FeatureGates.getExperimentValue('platform_editor_resolve_google_hyperlinks', 'isEnabled', false);
  var shouldResolveSharePoint = isSharePointDomain(href) && isSharePointResolveEnabled;
  var shouldResolveGoogle = isGoogleDomain(href) && isGoogleResolveEnabled;
  var shouldResolveHyperlink = hasSmartCardProvider && (shouldResolveSharePoint || shouldResolveGoogle);
  if (!shouldResolveHyperlink) {
    return null;
  }
  return /*#__PURE__*/React.createElement(ResolveHyperlink, {
    href: href
  });
};
export default withErrorBoundary(HyperlinkResolver);